import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Route } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ListItemText from '@material-ui/core/ListItemText';
import { translate as translateComponent, useDataProvider } from 'react-admin';
import { nanoid } from 'nanoid';
import { getUser } from '../../../../user';

const ACCOUNT_PAGINATION = { page: 1, perPage: 25 };
const ACCOUNT_SORT = { field: 'name', order: 'ASC' };

const buildAccountAddress = (account) => {
  if (!account) {
    return '';
  }

  if (account.shipping_address) {
    return account.shipping_address;
  }

  return [
    account.shipping_street,
    account.shipping_postal_code,
    account.shipping_city,
  ]
    .filter(Boolean)
    .join(' ');
};

const KeysFormButton = ({ className, record }) => {
  const user = getUser();
  const dataProvider = useDataProvider();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountOptions, setAccountOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const today = new Date();
  const displayYear = new Intl.DateTimeFormat('fr', { year: 'numeric' }).format(today);
  const displayMonth = new Intl.DateTimeFormat('fr', { month: 'long' }).format(today);
  const displayDay = new Intl.DateTimeFormat('fr', { day: '2-digit' }).format(today);
  const todayString = `${displayDay} ${displayMonth} ${displayYear}`;
  const isoYear = today.getFullYear();
  const isoMonth = `${today.getMonth() + 1}`.padStart(2, '0');
  const isoDay = `${today.getDate()}`.padStart(2, '0');
  const todayDateValue = `${isoYear}-${isoMonth}-${isoDay}`;

  const employeeAddress = `${record?.mailing_street} ${record?.mailing_city} ${record?.mailing_postal_code}`;
  const formUrl = 'https://forms.cleany.fr/remise-cles';

  useEffect(() => {
    let active = true;

    if (!dialogOpen) {
      return undefined;
    }

    const fetchAccounts = async () => {
      setLoading(true);
      setError(null);

      try {
        const filter = accountSearch ? { search: accountSearch } : {};
        const response = await dataProvider.getList('account', {
          pagination: ACCOUNT_PAGINATION,
          sort: ACCOUNT_SORT,
          filter,
        });

        if (!active) {
          return;
        }

        setAccountOptions(response.data || []);
      } catch (fetchError) {
        if (!active) {
          return;
        }

        setAccountOptions([]);
        setError(fetchError);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchAccounts();

    return () => {
      active = false;
    };
  }, [accountSearch, dataProvider, dialogOpen]);

  const handleClose = () => {
    setDialogOpen(false);
    setAccountSearch('');
    setSelectedAccount(null);
    setAccountOptions([]);
    setError(null);
  };

  const handleOpenForm = () => {
    if (!selectedAccount) {
      return;
    }

    const id = nanoid(18);
    const accountAddress = buildAccountAddress(selectedAccount);
    const params =
      `?formId=${id}` +
      `&employeeSilaeId=${record?.silae_id}` +
      `&todayDate=${todayDateValue}` +
      `&todaydate=${todayDateValue}` +
      `&todayDateLabel=${todayString}` +
      `&employeeFullname=${record?.fullname}` +
      `&employeeAddress=${employeeAddress}` +
      `&issuerFullname=${user?.fullname}` +
      `&accountName=${selectedAccount?.name}` +
      `&accountAddress=${accountAddress}`;

    window.open(`${formUrl}${params}`);
    handleClose();
  };

  return (
    <Fragment>
      <Route
        render={() => {
          return (
            <Fragment>
              <MenuItem
                className={className}
                variant="contained"
                onClick={() => {
                  setDialogOpen(true);
                }}
              >
                <ListItemText primary="Formulaire de remise de clés" />
              </MenuItem>
              <Dialog
                open={dialogOpen}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle>Choisir un compte</DialogTitle>
                <DialogContent>
                  <Autocomplete
                    options={accountOptions}
                    value={selectedAccount}
                    loading={loading}
                    getOptionLabel={(option) => {
                      return option && option.name ? option.name : '';
                    }}
                    getOptionSelected={(option, value) => {
                      return option && value && option.id === value.id;
                    }}
                    onChange={(_event, value) => {
                      setSelectedAccount(value);
                    }}
                    onInputChange={(_event, value) => {
                      setAccountSearch(value);
                    }}
                    noOptionsText={
                      loading ? 'Chargement...' : 'Aucun compte trouvé'
                    }
                    renderInput={(params) => {
                      return (
                        <TextField
                          {...params}
                          label="Compte"
                          placeholder="Rechercher un compte"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          error={Boolean(error)}
                          helperText={
                            error
                              ? 'Impossible de charger les comptes.'
                              : 'Sélectionnez le compte à associer au formulaire.'
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <Fragment>
                                {loading ? (
                                  <CircularProgress color="inherit" size={18} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </Fragment>
                            ),
                          }}
                        />
                      );
                    }}
                  />
                  {selectedAccount ? (
                    <Typography variant="body2" style={{ marginTop: 12 }}>
                      Adresse du compte : {buildAccountAddress(selectedAccount)}
                    </Typography>
                  ) : null}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose}>Annuler</Button>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleOpenForm}
                    disabled={!selectedAccount}
                  >
                    Ouvrir le formulaire
                  </Button>
                </DialogActions>
              </Dialog>
            </Fragment>
          );
        }}
      />
    </Fragment>
  );
};

KeysFormButton.propTypes = {
  className: PropTypes.string,
  record: PropTypes.object,
};

KeysFormButton.defaultProps = {
  className: undefined,
  record: undefined,
};

export default translateComponent(KeysFormButton);
